import { type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import { useCallback, useMemo, useState } from "react";

import { apixHandler } from "utilities/api/apix-handler";
import { reportError } from "utilities/errorHelpers";

import { type PlansProcessUploadRequest } from "handlers/models";
import { plansProcessPostV2, plansRevisionsProcessPostV2 } from "handlers/plans/plans";

import { type IPlanFileValidationResult, validatePlanFile } from "entity/Plans/utils/plans.utils";

import { type IPlanUploadItem } from "./PlansUploadModal.types";

export type UploadPhase = "idle" | "uploading" | "processing" | "done";

export interface IUploadResult {
    backgroundJobIds: string[];
    errors: Map<string, string>;
}

export interface IUsePlansUploadOptions {
    jobId: number;
}

export interface IUsePlansUploadResult {
    uploadFiles: (items: IPlanUploadItem[]) => Promise<IUploadResult>;
    progress: Map<string, number>;
    phase: UploadPhase;
    isUploading: boolean;
    validateFile: (file: File | BTFileSystem, validateType: boolean) => IPlanFileValidationResult;
    resetProgress: () => void;
}

export function usePlansUpload({ jobId }: IUsePlansUploadOptions): IUsePlansUploadResult {
    const [progress, setProgress] = useState<Map<string, number>>(new Map());
    const [isUploading, setIsUploading] = useState(false);

    const updateProgress = useCallback((itemId: string, percent: number) => {
        setProgress((prev) => {
            const next = new Map(prev);
            next.set(itemId, percent);
            return next;
        });
    }, []);

    const validateFile = useCallback((file: File | BTFileSystem, validateType: boolean) => {
        const fileName = file instanceof File ? file.name : file.fileName;
        const fileSize = file instanceof File ? file.size : file.fileSize;
        return validatePlanFile(fileName, fileSize, validateType);
    }, []);

    const uploadSingleItem = useCallback(
        async (item: IPlanUploadItem): Promise<{ itemId: string; backgroundJobId?: string }> => {
            updateProgress(item.id, 0);

            // A real target set routes the file through the mass-revisions AI review flow instead of
            // creating a new set.
            const isRevision = !!item.targetSetId;

            // Recipients to notify once a NEW set finishes processing. Only sent for new-set uploads;
            // revisions notify through the separate post-apply modal, so this stays empty for them.
            const notifyRecipientGlobalUserIds =
                !isRevision && item.notify ? (item.notifyRecipientGlobalUserIds ?? []) : [];

            // A BT-sourced file is already in Buildertrend storage, so it skips the temp-upload step and
            // is referenced by its documentInstanceId — for both the new-set and the revisions flow.
            if (item.documentInstanceId != null) {
                const processResponse = isRevision
                    ? await plansRevisionsProcessPostV2(
                          { documentInstanceId: item.documentInstanceId, setId: item.targetSetId },
                          { jobId },
                      )
                    : await plansProcessPostV2(
                          {
                              documentInstanceId: item.documentInstanceId,
                              notifyRecipientGlobalUserIds,
                          },
                          { jobId },
                      );
                updateProgress(item.id, 100);
                return {
                    itemId: item.id,
                    backgroundJobId: processResponse.data.backgroundJobId,
                };
            }

            if (isRevision) {
                if (!item.uploadFile?.originFileObj) {
                    throw new Error(`Revisions upload requires a file for ${item.fileName}`);
                }

                const file = item.uploadFile.originFileObj;
                const tempUploadResponse = await apixHandler<PlansProcessUploadRequest>({
                    url: "/apix/v2/Documents/temp-upload",
                    method: "POST",
                    headers: { "Content-Type": "application/octet-stream" },
                    data: file,
                    params: { fileName: file.name },
                    transformRequest: [(data: unknown) => data],
                    onUploadProgress: (progressEvent) => {
                        const total = progressEvent.total ?? file.size;
                        const percent = Math.round((progressEvent.loaded * 90) / total);
                        updateProgress(item.id, percent);
                    },
                });

                updateProgress(item.id, 95);

                const processResponse = await plansRevisionsProcessPostV2(
                    {
                        tempFilePath: tempUploadResponse.data.tempFilePath,
                        fileName: tempUploadResponse.data.fileName,
                        contentType: tempUploadResponse.data.contentType,
                        fileLength: tempUploadResponse.data.fileLength,
                        setId: item.targetSetId,
                    },
                    { jobId },
                );

                updateProgress(item.id, 100);
                return {
                    itemId: item.id,
                    backgroundJobId: processResponse.data.backgroundJobId,
                };
            }

            if (item.uploadFile?.originFileObj) {
                const file = item.uploadFile.originFileObj;
                const tempUploadResponse = await apixHandler<PlansProcessUploadRequest>({
                    url: "/apix/v2/Documents/temp-upload",
                    method: "POST",
                    headers: { "Content-Type": "application/octet-stream" },
                    data: file,
                    params: { fileName: file.name },
                    // File/Blob objects must bypass defaultTransformRequest, which JSON.stringify's all
                    // non-FormData payloads — serializing a File produces {} instead of binary content.
                    transformRequest: [(data: unknown) => data],
                    onUploadProgress: (progressEvent) => {
                        const total = progressEvent.total ?? file.size;
                        // Upload transfer is 0-90%, processing is the remaining 10%
                        const percent = Math.round((progressEvent.loaded * 90) / total);
                        updateProgress(item.id, percent);
                    },
                });

                updateProgress(item.id, 95);

                const processResponse = await plansProcessPostV2(
                    {
                        tempFilePath: tempUploadResponse.data.tempFilePath,
                        fileName: tempUploadResponse.data.fileName,
                        contentType: tempUploadResponse.data.contentType,
                        fileLength: tempUploadResponse.data.fileLength,
                        notifyRecipientGlobalUserIds,
                    },
                    { jobId },
                );

                updateProgress(item.id, 100);
                return {
                    itemId: item.id,
                    backgroundJobId: processResponse.data.backgroundJobId,
                };
            }

            throw new Error(`No valid file data for ${item.fileName}`);
        },
        [jobId, updateProgress],
    );

    const uploadFiles = useCallback(
        async (items: IPlanUploadItem[]): Promise<IUploadResult> => {
            setIsUploading(true);
            const backgroundJobIds: string[] = [];
            const errors = new Map<string, string>();

            try {
                const results = await Promise.allSettled(
                    items.map((item) => uploadSingleItem(item)),
                );

                results.forEach((result, index) => {
                    const item = items[index];
                    if (result.status === "fulfilled") {
                        if (result.value.backgroundJobId) {
                            backgroundJobIds.push(result.value.backgroundJobId);
                        }
                    } else {
                        const error: unknown = result.reason;
                        const errorMessage =
                            error instanceof Error ? error.message : "Upload failed";
                        errors.set(item.id, errorMessage);

                        const fileType =
                            item.documentInstanceId != null ? "BT file" : "computer file";
                        reportError(error, {
                            internalNotes: `Plan upload failed for ${fileType}: ${item.fileName}, jobId: ${jobId}`,
                        });
                    }
                });

                return { backgroundJobIds, errors };
            } finally {
                setIsUploading(false);
            }
        },
        [uploadSingleItem, jobId],
    );

    const phase = useMemo<UploadPhase>(() => {
        if (progress.size === 0) {
            return "idle";
        }
        const values = Array.from(progress.values());
        if (values.every((p) => p === 100)) {
            return isUploading ? "processing" : "done";
        }
        if (values.every((p) => p >= 90)) {
            return "processing";
        }
        return "uploading";
    }, [progress, isUploading]);

    const resetProgress = useCallback(() => {
        setProgress(new Map());
    }, []);

    return {
        uploadFiles,
        progress,
        phase,
        isUploading,
        validateFile,
        resetProgress,
    };
}
